import React, { useEffect, useState } from 'react';
import api from '../api/api';
import {
  formatDate,
  formatBundleRule,
  getActiveStateLabel,
  getBundleScopeLabel,
  getBundleTypeLabel,
  translateApiMessage,
} from '../utils/dashboardTranslations';

const initialForm = {
  name: '',
  description: '',
  bundleType: 'BuyXGetY',
  buyQuantity: '',
  getQuantity: '',
  getDiscountPercentage: '100',
  bundlePrice: '',
  scope: 'Product',
  productId: '',
  vendorId: '',
  vendorCategoryId: '',
  startDate: '',
  endDate: '',
  isActive: true,
};

function BundleDeals() {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deals, setDeals] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [editId, setEditId] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [productVendorFilter, setProductVendorFilter] = useState('');
  const [productCategoryFilter, setProductCategoryFilter] = useState('');

  useEffect(() => {
    fetchLookups();
    fetchDeals();
  }, []);

  const fetchLookups = async () => {
    try {
      const [vendorsRes, productsRes, categoriesRes] = await Promise.all([
        api.get('/Vendor'),
        api.get('/Product'),
        api.get('/VendorCategory'),
      ]);
      setVendors(Array.isArray(vendorsRes.data) ? vendorsRes.data : vendorsRes.data?.vendors || []);
      setProducts(Array.isArray(productsRes.data) ? productsRes.data : productsRes.data?.items || []);
      setCategories(Array.isArray(categoriesRes.data) ? categoriesRes.data : categoriesRes.data?.categories || []);
    } catch (error) {
      console.error('Error loading bundle lookups:', error);
      alert('فشل تحميل بيانات القوائم المساعدة');
    }
  };

  const fetchDeals = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/bundle-deals');
      setDeals(response.data?.items || []);
    } catch (error) {
      console.error('Error loading bundle deals:', error);
      alert('فشل تحميل عروض الحزم');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm(initialForm);
    setEditId(null);
    setProductVendorFilter('');
    setProductCategoryFilter('');
  };

  const filteredProducts = products.filter((product) => {
    const matchesVendor = productVendorFilter ? String(product.vendorId || '') === productVendorFilter : true;
    const effectiveCategoryId = product.vendorCategoryId || product.vendor?.vendorCategoryId || '';
    const matchesCategory = productCategoryFilter ? String(effectiveCategoryId) === productCategoryFilter : true;
    return matchesVendor && matchesCategory;
  });

  const getProductName = (productId) => {
    if (!productId) return '—';
    return products.find((product) => String(product.id) === String(productId))?.name || `#${productId}`;
  };

  const getVendorName = (deal) => {
    if (!deal) return '—';

    if (deal.scope === 'Vendor' && deal.vendorId) {
      return vendors.find((vendor) => String(vendor.id) === String(deal.vendorId))?.name || `#${deal.vendorId}`;
    }

    if (deal.scope === 'Product' && deal.productId) {
      const product = products.find((item) => String(item.id) === String(deal.productId));
      if (!product) return '—';

      if (product.vendor?.name) return product.vendor.name;
      if (product.vendorId) {
        return vendors.find((vendor) => String(vendor.id) === String(product.vendorId))?.name || `#${product.vendorId}`;
      }
    }

    return '—';
  };

  const validateForm = () => {
    if (!form.name.trim()) return 'الاسم مطلوب';
    if (!form.buyQuantity || Number(form.buyQuantity) <= 0) return 'كمية الشراء مطلوبة';
    if (form.bundleType === 'BuyXGetY' && (!form.getQuantity || Number(form.getQuantity) <= 0)) {
      return 'كمية الهدية مطلوبة';
    }
    if (form.bundleType === 'BuyXForPrice' && (!form.bundlePrice || Number(form.bundlePrice) < 0)) {
      return 'سعر العرض مطلوب';
    }
    if (form.startDate && form.endDate && new Date(form.endDate) <= new Date(form.startDate)) {
      return 'تاريخ النهاية يجب أن يكون بعد تاريخ البدء';
    }
    if (form.scope === 'Product' && !form.productId) return 'يرجى اختيار منتج';
    if (form.scope === 'Vendor' && !form.vendorId) return 'يرجى اختيار بائع';
    if (form.scope === 'Category' && !form.vendorCategoryId) return 'يرجى اختيار فئة';
    return null;
  };

  const buildPayload = () => ({
    name: form.name,
    description: form.description || null,
    bundleType: form.bundleType,
    buyQuantity: Number(form.buyQuantity),
    getQuantity: form.bundleType === 'BuyXGetY' ? Number(form.getQuantity || 0) : 0,
    getDiscountPercentage: form.bundleType === 'BuyXGetY' ? Number(form.getDiscountPercentage || 100) : null,
    bundlePrice: form.bundleType === 'BuyXForPrice' ? Number(form.bundlePrice) : null,
    scope: form.scope,
    productId: form.scope === 'Product' ? Number(form.productId) : null,
    vendorId: form.scope === 'Vendor' ? Number(form.vendorId) : null,
    vendorCategoryId: form.scope === 'Category' ? Number(form.vendorCategoryId) : null,
    startDate: form.startDate || null,
    endDate: form.endDate || null,
    isActive: !!form.isActive,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validateForm();
    if (validationError) {
      alert(validationError);
      return;
    }

    try {
      setSaving(true);
      const payload = buildPayload();
      if (editId) {
        await api.put(`/admin/bundle-deals/${editId}`, payload);
        alert('تم تحديث العرض بنجاح');
      } else {
        await api.post('/admin/bundle-deals', payload);
        alert('تم إنشاء العرض بنجاح');
      }
      resetForm();
      fetchDeals();
    } catch (error) {
      console.error('Error saving bundle deal:', error);
      alert(
        translateApiMessage(error?.response?.data?.message || error?.response?.data) || 'فشل حفظ العرض'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (deal) => {
    const selectedProduct = products.find((product) => product.id === deal.productId);
    const selectedProductCategoryId = selectedProduct
      ? String(selectedProduct.vendorCategoryId || selectedProduct.vendor?.vendorCategoryId || '')
      : '';
    const selectedProductVendorId = selectedProduct?.vendorId ? String(selectedProduct.vendorId) : '';

    setEditId(deal.id);
    setForm({
      name: deal.name || '',
      description: deal.description || '',
      bundleType: deal.bundleType || 'BuyXGetY',
      buyQuantity: deal.buyQuantity ?? '',
      getQuantity: deal.getQuantity ?? '',
      getDiscountPercentage: deal.getDiscountPercentage ?? '100',
      bundlePrice: deal.bundlePrice ?? '',
      scope: deal.scope || 'Product',
      productId: deal.productId ?? '',
      vendorId: deal.vendorId ?? '',
      vendorCategoryId: deal.vendorCategoryId ?? '',
      startDate: deal.startDate ? String(deal.startDate).slice(0, 10) : '',
      endDate: deal.endDate ? String(deal.endDate).slice(0, 10) : '',
      isActive: deal.isActive !== false,
    });
    setProductVendorFilter(selectedProductVendorId);
    setProductCategoryFilter(selectedProductCategoryId);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeactivate = async (dealId) => {
    if (!window.confirm('هل تريد تعطيل هذا العرض؟')) return;
    try {
      await api.delete(`/admin/bundle-deals/${dealId}`);
      fetchDeals();
    } catch (error) {
      console.error('Error deactivating bundle deal:', error);
      alert('فشل تعطيل العرض');
    }
  };

  return (
    <div>
      <h4 className="mb-3">إدارة عروض الحزم</h4>

      <div className="card mb-4">
        <div className="card-header d-flex justify-content-between align-items-center">
          <strong>{editId ? 'تعديل العرض' : 'إضافة عرض جديد'}</strong>
          {editId && (
            <button className="btn btn-outline-secondary btn-sm" onClick={resetForm}>
              إلغاء التعديل
            </button>
          )}
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            <div className="row g-3">
              <div className="col-md-4">
                <label className="form-label">الاسم</label>
                <input
                  className="form-control"
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  required
                />
              </div>

              <div className="col-md-4">
                <label className="form-label">نوع العرض</label>
                <select
                  className="form-select"
                  value={form.bundleType}
                  onChange={(e) => setForm((p) => ({ ...p, bundleType: e.target.value }))}
                >
                  <option value="BuyXGetY">اشترِ X واحصل على Y</option>
                  <option value="BuyXForPrice">اشترِ X بسعر محدد</option>
                </select>
              </div>

              <div className="col-md-4">
                <label className="form-label">كمية الشراء</label>
                <input
                  type="number"
                  className="form-control"
                  value={form.buyQuantity}
                  onChange={(e) => setForm((p) => ({ ...p, buyQuantity: e.target.value }))}
                  required
                />
              </div>

              {form.bundleType === 'BuyXGetY' ? (
                <>
                  <div className="col-md-4">
                    <label className="form-label">كمية الهدية</label>
                    <input
                      type="number"
                      className="form-control"
                      value={form.getQuantity}
                      onChange={(e) => setForm((p) => ({ ...p, getQuantity: e.target.value }))}
                      required
                    />
                  </div>

                  <div className="col-md-4">
                    <label className="form-label">نسبة خصم الهدية %</label>
                    <input
                      type="number"
                      className="form-control"
                      value={form.getDiscountPercentage}
                      onChange={(e) => setForm((p) => ({ ...p, getDiscountPercentage: e.target.value }))}
                    />
                  </div>
                </>
              ) : (
                <div className="col-md-4">
                  <label className="form-label">سعر العرض</label>
                  <input
                    type="number"
                    className="form-control"
                    value={form.bundlePrice}
                    onChange={(e) => setForm((p) => ({ ...p, bundlePrice: e.target.value }))}
                    required
                  />
                </div>
              )}

              <div className="col-md-12">
                <label className="form-label">الوصف</label>
                <input
                  className="form-control"
                  value={form.description}
                  onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                />
              </div>

              <div className="col-md-3">
                <label className="form-label">النطاق</label>
                <select
                  className="form-select"
                  value={form.scope}
                  onChange={(e) => setForm((p) => ({ ...p, scope: e.target.value }))}
                >
                  <option value="Product">منتج</option>
                  <option value="Vendor">بائع</option>
                  <option value="Category">فئة</option>
                </select>
              </div>

              {form.scope === 'Product' && (
                <>
                  <div className="col-md-3">
                    <label className="form-label">تصفية حسب الفئة</label>
                    <select
                      className="form-select"
                      value={productCategoryFilter}
                      onChange={(e) => {
                        const nextCategory = e.target.value;
                        setProductCategoryFilter(nextCategory);
                        setForm((p) => ({ ...p, productId: '' }));
                      }}
                    >
                      <option value="">كل الفئات</option>
                      {categories.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-3">
                    <label className="form-label">تصفية حسب البائع</label>
                    <select
                      className="form-select"
                      value={productVendorFilter}
                      onChange={(e) => {
                        const nextVendor = e.target.value;
                        setProductVendorFilter(nextVendor);
                        setForm((p) => ({ ...p, productId: '' }));
                      }}
                    >
                      <option value="">كل البائعين</option>
                      {vendors.map((v) => (
                        <option key={v.id} value={v.id}>{v.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">المنتج</label>
                    <select
                      className="form-select"
                      value={form.productId}
                      onChange={(e) => setForm((p) => ({ ...p, productId: e.target.value }))}
                      required
                    >
                      <option value="">اختر</option>
                      {filteredProducts.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                          {p.vendor?.name ? ` - ${p.vendor.name}` : ''}
                          {p.vendorCategory?.name ? ` - ${p.vendorCategory.name}` : ''}
                        </option>
                      ))}
                    </select>
                    <small className="text-muted">
                      المنتجات المعروضة: {filteredProducts.length}
                    </small>
                  </div>
                </>
              )}

              {form.scope === 'Vendor' && (
                <div className="col-md-3">
                  <label className="form-label">البائع</label>
                  <select
                    className="form-select"
                    value={form.vendorId}
                    onChange={(e) => setForm((p) => ({ ...p, vendorId: e.target.value }))}
                    required
                  >
                    <option value="">اختر</option>
                    {vendors.map((v) => (
                      <option key={v.id} value={v.id}>{v.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {form.scope === 'Category' && (
                <div className="col-md-3">
                  <label className="form-label">الفئة</label>
                  <select
                    className="form-select"
                    value={form.vendorCategoryId}
                    onChange={(e) => setForm((p) => ({ ...p, vendorCategoryId: e.target.value }))}
                    required
                  >
                    <option value="">اختر</option>
                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="col-md-3">
                <label className="form-label">تاريخ البدء</label>
                <input
                  type="date"
                  className="form-control"
                  value={form.startDate}
                  onChange={(e) => setForm((p) => ({ ...p, startDate: e.target.value }))}
                />
              </div>

              <div className="col-md-3">
                <label className="form-label">تاريخ الانتهاء</label>
                <input
                  type="date"
                  className="form-control"
                  value={form.endDate}
                  onChange={(e) => setForm((p) => ({ ...p, endDate: e.target.value }))}
                />
              </div>

              <div className="col-md-3 d-flex align-items-end">
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={form.isActive}
                    onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.checked }))}
                  />
                  <label className="form-check-label">فعّال</label>
                </div>
              </div>

              <div className="col-md-12 text-end">
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'جاري الحفظ...' : editId ? 'حفظ التعديل' : 'إنشاء العرض'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      <div className="card">
        <div className="card-header"><strong>قائمة عروض الحزم</strong></div>
        <div className="card-body">
          {loading ? (
            <div className="text-center py-4">جاري التحميل...</div>
          ) : deals.length === 0 ? (
            <div className="text-center text-muted py-4">لا توجد عروض حالياً</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover table-striped">
                <thead className="table-light">
                  <tr>
                    <th>الاسم</th>
                    <th>المنتج</th>
                    <th>البائع / المتجر</th>
                    <th>النوع</th>
                    <th>القاعدة</th>
                    <th>النطاق</th>
                    <th>الفترة</th>
                    <th>الحالة</th>
                    <th>الإجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {deals.map((deal) => (
                    <tr key={deal.id}>
                      <td>{deal.name}</td>
                      <td>{deal.scope === 'Product' ? getProductName(deal.productId) : '—'}</td>
                      <td>{getVendorName(deal)}</td>
                      <td>{getBundleTypeLabel(deal.bundleType)}</td>
                      <td>{formatBundleRule(deal)}</td>
                      <td>{getBundleScopeLabel(deal.scope)}</td>
                      <td>
                        {formatDate(deal.startDate)}
                        {' - '}
                        {formatDate(deal.endDate)}
                      </td>
                      <td>
                        <span className={`badge ${deal.isActive ? 'bg-success' : 'bg-secondary'}`}>
                          {getActiveStateLabel(deal.isActive)}
                        </span>
                      </td>
                      <td>
                        <div className="btn-group btn-group-sm">
                          <button className="btn btn-outline-primary" onClick={() => handleEdit(deal)}>تعديل</button>
                          <button className="btn btn-outline-danger" onClick={() => handleDeactivate(deal.id)}>تعطيل</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default BundleDeals;
